import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { articles } from "../articals/articles";
import ContactForm from "../Component/ContactForm";
import { Helmet } from 'react-helmet-async';

const networkNodes = [
    { id: 1, x: 100, y: 100, label: "LOGIC" },
    { id: 2, x: 250, y: 50, label: "AI_CORE" },
    { id: 3, x: 350, y: 150, label: "SAAS" },
    { id: 4, x: 300, y: 300, label: "SCALABILITY" },
    { id: 5, x: 150, y: 250, label: "UX_FLOW" },
    { id: 6, x: 50, y: 200, label: "INFERENCE" }
];
const connections = [
    { from: 0, to: 1 }, { from: 1, to: 2 }, { from: 2, to: 3 },
    { from: 3, to: 4 }, { from: 4, to: 5 }, { from: 5, to: 0 },
    { from: 1, to: 4 }, { from: 2, to: 5 }
];

const BlogsPage = () => {
    const [activeCategory, setActiveCategory] = useState("All");
    const [categories, setCategories] = useState([]);

    // 1. Extract Unique Categories (Source of Truth)
    useEffect(() => {
        const uniqueCategories = [
            "All",
            ...new Set(
                articles
                    .map((item) => item.category)
                    .filter(Boolean) // Cleaner way to remove null/undefined/empty
            )
        ];
        setCategories(uniqueCategories);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    // 2. Optimized Filtering Logic (Filtering by Category, not Tags)
    const filteredArticles = useMemo(() => {
        if (!articles) return [];

        return articles.filter(art => {
            if (activeCategory === "All") return true;
            // Match the article's category with the active state
            return art.category === activeCategory;
        });
    }, [activeCategory]);

    return (
        <>
            <Helmet>
                <title>Tech, Travel & Life Stories | A Developer’s Personal Blog</title>
                <meta name="description" content="Explore real stories of becoming a web developer, startup life, lifestyle, self-growth, and creativity." />
            </Helmet>
            <main className="min-h-screen bg-white dark:bg-[#080a0f] pt-28 overflow-hidden font-sans selection:bg-primary/30">
                <header className="w-[90%] mx-auto mb-20 relative pt-10">
                    {/* 1. Neuro-Networking Visual (Right Side) */}
                    <div className="absolute -top-10 right-0 w-full lg:w-1/3 h-[400px] pointer-events-none hidden lg:block overflow-visible">
                        <svg viewBox="0 0 400 400" className="w-full h-full text-primary opacity-40 dark:opacity-20">

                            {/* Connection Lines (Synapses) */}
                            {connections.map((conn, i) => (
                                <g key={`conn-${i}`}>
                                    <motion.line
                                        x1={networkNodes[conn.from].x} y1={networkNodes[conn.from].y}
                                        x2={networkNodes[conn.to].x} y2={networkNodes[conn.to].y}
                                        stroke="currentColor" strokeWidth="0.5"
                                        initial={{ pathLength: 0, opacity: 0 }}
                                        animate={{ pathLength: 1, opacity: 0.3 }}
                                        transition={{ duration: 2, delay: i * 0.2 }}
                                    />
                                    {/* Traveling Data Pulse */}
                                    <motion.circle
                                        r="2" fill="currentColor"
                                        animate={{
                                            cx: [networkNodes[conn.from].x, networkNodes[conn.to].x],
                                            cy: [networkNodes[conn.from].y, networkNodes[conn.to].y],
                                            opacity: [0, 1, 0]
                                        }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            delay: i * 0.5,
                                            ease: "linear"
                                        }}
                                    />
                                </g>
                            ))}

                            {/* Nodes (Neural Hubs) */}
                            {networkNodes.map((node, i) => (
                                <g key={`node-${i}`}>
                                    {/* Outer Glow Ring */}
                                    <motion.circle
                                        cx={node.x} cy={node.y} r="8"
                                        fill="none" stroke="currentColor" strokeWidth="0.5"
                                        animate={{ scale: [1, 1.5, 1], opacity: [0.1, 0.4, 0.1] }}
                                        transition={{ duration: 3, repeat: Infinity, delay: i * 0.3 }}
                                    />
                                    {/* Solid Core */}
                                    <circle cx={node.x} cy={node.y} r="3" fill="currentColor" className="shadow-glow" />

                                    {/* Technical Labels */}
                                    <motion.text
                                        x={node.x + 10} y={node.y + 5}
                                        fontSize="8"
                                        fontFamily="monospace"
                                        fontWeight="900"
                                        fill="currentColor"
                                        className="tracking-tighter opacity-60"
                                        animate={{ opacity: [0.3, 0.8, 0.3] }}
                                        transition={{ duration: 4, repeat: Infinity, delay: i * 0.5 }}
                                    >
                                        {node.label}
                                    </motion.text>
                                </g>
                            ))}
                        </svg>
                    </div>

                    {/* 2. Content Section (Stays Identical for consistency) */}
                    <div className="flex flex-col lg:flex-row justify-between items-end gap-12 relative z-10">
                        <div className="w-full lg:max-w-3xl">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                className="flex items-center gap-3 mb-6"
                            >
                                <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-black uppercase tracking-[0.2em]">
                                    Learning in Public
                                </span>
                                <span className="h-px w-12 bg-gray-200 dark:bg-white/10"></span>
                                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">
                                    Updated: {new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                                </span>
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#0d121b] dark:text-white font-display tracking-tighter leading-[0.9] mb-8"
                            >
                                From Curiosity <br />
                                <span className="text-primary italic">to Systems Thinking.</span>
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                className="text-gray-500 dark:text-gray-400 text-lg md:text-xl leading-relaxed font-medium max-w-xl"
                            >
                                Documenting my transition from a{" "}
                                <span className="text-primary font-bold">non-tech mindset</span> to building{" "}
                                <span className="text-primary font-bold">logical, scalable products</span> -
                                blending casual curiosity with structured thinking through{" "}
                                <span className="text-primary font-bold">AI, SaaS</span>, and real projects like{" "}
                                <span className="text-primary font-bold">ContactHQ</span>.
                            </motion.p>
                        </div>

                        {/* --- Stats Cards --- */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full lg:w-auto">
                            <motion.div
                                whileHover={{ y: -5 }}
                                className="p-6 rounded-3xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 shadow-soft"
                            >
                                <p className="text-[10px] font-black uppercase text-gray-400 mb-2">Writing Since</p>
                                <p className="text-2xl font-bold dark:text-white font-display">Learning Phase</p>
                            </motion.div>

                            <motion.div
                                whileHover={{ y: -5 }}
                                className="p-6 rounded-3xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 shadow-soft"
                            >
                                <p className="text-[10px] font-black uppercase text-gray-400 mb-2">Current Focus</p>
                                <p className="text-2xl font-bold text-primary font-display">AI • SaaS</p>
                            </motion.div>
                        </div>
                    </div>
                </header>

                {/* --- 2. Responsive Navigation Hub --- */}
                <nav className="w-[90%] mx-auto mb-16 sticky top-24 z-40">
                    <div className="bg-white/70 dark:bg-[#080a0f]/80 backdrop-blur-2xl rounded-3xl border border-gray-100 dark:border-white/10 p-2 shadow-2xl">
                        <div className="flex flex-nowrap overflow-x-auto lg:justify-center gap-1 no-scrollbar p-1">
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    className={`whitespace-nowrap px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all duration-500 flex-shrink-0 ${activeCategory === cat
                                        ? "bg-primary text-white shadow-glow shadow-primary/40 scale-105"
                                        : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5"
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>
                </nav>

                {/* --- 3. The Storyline (Fully Responsive Grid) --- */}
                <section className="w-[90%] mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                    <AnimatePresence mode="popLayout">
                        {filteredArticles.map((article, index) => (
                            <motion.div
                                key={article.id}
                                layout
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="group"
                            >
                                <Link to={`/blog/${article.slug || article.id}`} className="block relative">
                                    {/* The Paper-Style Card */}
                                    <div className="relative bg-white dark:bg-[#0c121d] border border-gray-100 dark:border-white/5 p-6
                                    shadow-[8px_8px_0px_0px_rgba(0,0,0,0.03)] dark:shadow-[8px_8px_0px_0px_rgba(255,255,255,0.01)]
                                    group-hover:shadow-[12px_12px_0px_0px_rgba(var(--primary-rgb),0.1)]
                                    group-hover:-translate-y-1 group-hover:-translate-x-1
                                    transition-all duration-300 overflow-hidden"
                                    >
                                        <div className="flex flex-col sm:flex-row gap-6">
                                            {/* Thumbnail Piece */}
                                            <div className="w-full sm:w-32 h-32 shrink-0 bg-gray-50 dark:bg-white/5 rounded-sm overflow-hidden border border-gray-100 dark:border-white/5">
                                                <img
                                                    src={article.thumbnail}
                                                    alt={article.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                />
                                            </div>

                                            {/* Information Content */}
                                            <div className="flex-grow">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-[10px] font-black text-primary uppercase tracking-widest">{article.tags[0]}</span>
                                                    <span className="text-[9px] font-mono text-gray-400">INDEX_{String(index + 1).padStart(2, '0')}</span>
                                                </div>
                                                <h3 className="text-xl font-bold dark:text-white font-display leading-tight mb-2 group-hover:text-primary transition-colors">
                                                    {article.title}
                                                </h3>
                                                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed italic mb-4">
                                                    "{article.excerpt}"
                                                </p>
                                                <div className="flex items-center justify-between pt-4 border-t border-dashed border-gray-100 dark:border-white/5">
                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{article.publishedDate}</span>
                                                    <span className="text-[10px] font-black text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                                                        LOG_ENTRY <span className="material-symbols-outlined text-xs">arrow_right_alt</span>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </section>

                {/* --- 4. Emotional Footer Note --- */}
                <footer className="w-[90%] mx-auto my-20 pt-20 border-t border-gray-100 dark:border-white/5 text-center">
                    <div className="max-w-xl mx-auto">
                        <span className="material-symbols-outlined text-primary text-4xl mb-6">
                            history_edu
                        </span>
                        <h3 className="text-2xl font-bold dark:text-white font-display mb-4">
                            Still learning. Still writing.
                        </h3>
                        <p className="text-gray-500 font-medium italic">
                            Each post captures a moment - from confusion to clarity, from ideas to systems.
                            This blog grows as I do.
                        </p>
                    </div>
                </footer>


                {/* --- Contact Section --- */}
                <section
                    id="contact"
                    className="relative py-32 bg-[#0d121b] dark:bg-black text-white overflow-hidden scroll-mt-20"
                >
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4 -z-0"></div>

                    <div className="w-[90%] mx-auto relative z-10">
                        <div className="grid lg:grid-cols-2 gap-16 items-start">
                            <div className="space-y-8">
                                <div className="reveal inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest font-display">
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                                    </span>
                                    Let's Connect
                                </div>

                                <h2 className="reveal reveal-delay-1 text-5xl md:text-7xl font-bold font-display leading-[1.1] tracking-tight">
                                    Let's build something <span className="text-primary">legendary.</span>
                                </h2>

                                <div className="reveal reveal-delay-3 space-y-6 pt-4">
                                    <a
                                        className="flex items-center gap-5 text-xl font-medium hover:text-primary transition-all group font-display"
                                        href="mailto:rahul@contacthq.in"
                                    >
                                        <span className="size-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:border-primary/50 transition-all">
                                            <span className="material-symbols-outlined text-primary text-3xl">
                                                mail
                                            </span>
                                        </span>
                                        rahul@contacthq.in
                                    </a>

                                    <div className="flex flex-wrap gap-4 pt-4">
                                        {/* LinkedIn */}
                                        <a
                                            href="https://www.linkedin.com/in/rahulbiswas0675/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="size-14 rounded-2xl bg-white/5 border border-white/10 hover:bg-[#0077b5] hover:border-[#0077b5] flex items-center justify-center transition-all group"
                                            title="LinkedIn"
                                        >
                                            <svg className="w-6 h-6 fill-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                                                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                            </svg>
                                        </a>

                                        {/* Instagram */}
                                        <a
                                            href="https://www.instagram.com/rahul.lens.ig/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="size-14 rounded-2xl bg-white/5 border border-white/10 hover:bg-[#e1306c] hover:border-[#e1306c] flex items-center justify-center transition-all group"
                                            title="Instagram"
                                        >
                                            <svg className="w-6 h-6 fill-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                            </svg>
                                        </a>

                                        {/* X (Twitter) */}
                                        <a
                                            href="https://x.com/RahulBiswas0675"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="size-14 rounded-2xl bg-white/5 border border-white/10 hover:bg-white hover:border-white flex items-center justify-center transition-all group"
                                            title="X (Twitter)"
                                        >
                                            <svg className="w-5 h-5 fill-white group-hover:fill-black group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                                                <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.294 19.497h2.039L6.486 3.24H4.298l13.309 17.41z" />
                                            </svg>
                                        </a>

                                        {/* YouTube */}
                                        <a
                                            href="https://www.youtube.com/@B4_I_Sleep"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="size-14 rounded-2xl bg-white/5 border border-white/10 hover:bg-[#ff0000] hover:border-[#ff0000] flex items-center justify-center transition-all group"
                                            title="YouTube"
                                        >
                                            <svg className="w-6 h-6 fill-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                            </svg>
                                        </a>

                                        {/* Facebook */}
                                        <a
                                            href="https://www.facebook.com/rahulbiswas0675"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="size-14 rounded-2xl bg-white/5 border border-white/10 hover:bg-[#1877f2] hover:border-[#1877f2] flex items-center justify-center transition-all group"
                                            title="Facebook"
                                        >
                                            <svg className="w-6 h-6 fill-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                            </svg>
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="reveal reveal-delay-2 relative">
                                <ContactForm />
                            </div>
                        </div>
                    </div>
                </section>

            </main>
        </>
    );
};

export default BlogsPage;